<template>
  <div class="container mt-5 px-4">
    <div class="row justify-content-center">
      <!-- Sidebar / Menu -->
      <div class="col-lg-4 col-md-5 mb-4">
        <div class="card shadow-sm border-0 rounded-3 overflow-hidden">
          <div class="card-body p-0">
            <div class="text-center p-4 bg-light border-bottom">
              <div class="avatar-circle mx-auto bg-primary text-white d-flex align-items-center justify-content-center fs-1 fw-bold shadow-sm">
                {{ $store.getters.getUserInfo.name ? $store.getters.getUserInfo.name.charAt(0).toUpperCase() : 'U' }}
              </div>
              <h4 class="fw-bold mt-3 mb-1 text-dark">{{ $store.getters.getUserInfo.name }}</h4>
              <p class="text-muted small mb-0">{{ $store.getters.getUserInfo.userType }}</p>
            </div>
            
            <div class="list-group list-group-flush p-3">
              <button @click="$router.push('profile')" class="list-group-item list-group-item-action border-0 rounded-2 mb-2 d-flex align-items-center text-muted">
                <span class="icon-wrapper me-3 d-flex align-items-center justify-content-center rounded">
                  <font-awesome-icon :icon="['fas', 'user']" />
                </span>
                Profile Information
              </button>
              <button class="list-group-item list-group-item-action active border-0 rounded-2 d-flex align-items-center">
                <span class="icon-wrapper me-3 d-flex align-items-center justify-content-center rounded">
                  <font-awesome-icon :icon="['fas', 'lock']" />
                </span>
                Change Password
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Main Content -->
      <div class="col-lg-8 col-md-7">
        <div class="card shadow-sm border-0 rounded-3 h-100">
          <div class="card-header bg-white border-bottom-0 pt-4 pb-0 px-4">
            <h4 class="fw-bold mb-0 text-dark">Change Password</h4>
          </div>
          <div class="card-body p-4">
            <div class="form-group mb-3">
              <label for="inputPasswordOld" class="form-label fw-semibold text-muted">Old Password <span class="text-danger">*</span></label>
              <div class="position-relative mt-1">
                <input
                    v-model="dataParams.oldPassword"
                    :type="dataParams.showOldPassword ? 'text' : 'password'"
                    placeholder="Enter old password"
                    autocomplete="on"
                    class="form-control form-control-lg font_detail bg-light border-0"
                    style="padding-right: 45px;"
                />
                <span class="position-absolute" style="right: 15px; top: 50%; transform: translateY(-50%); cursor: pointer; color: #6c757d;"
                       @click="dataParams.showOldPassword = !dataParams.showOldPassword">
                  <font-awesome-icon :icon="['fas', dataParams.showOldPassword ? 'eye-slash' : 'eye']" />
                </span>
              </div>
              <Error v-if="errors.oldPassword" v-bind:error="errors.oldPassword"/> 
            </div>
            <div class="form-group mb-3">
              <label for="inputPasswordNew" class="form-label fw-semibold text-muted">New Password <span class="text-danger">*</span></label>
              <div class="position-relative mt-1">
                <input
                    v-model="dataParams.newPassword"
                    :type="dataParams.showNewPassword ? 'text' : 'password'"
                    placeholder="Enter new password"
                    autocomplete="on"
                    class="form-control form-control-lg font_detail bg-light border-0"
                    style="padding-right: 45px;"
                    @keyup="checkConfirmPassword"
                />
                <span class="position-absolute" style="right: 15px; top: 50%; transform: translateY(-50%); cursor: pointer; color: #6c757d;"
                       @click="dataParams.showNewPassword = !dataParams.showNewPassword">
                  <font-awesome-icon :icon="['fas', dataParams.showNewPassword ? 'eye-slash' : 'eye']" />
                </span>
              </div>
              <Error v-if="errors.newPassword" v-bind:error="errors.newPassword"/>
            </div>

            <div class="form-group mb-4">
              <label for="inputPasswordConfirm" class="form-label fw-semibold text-muted">Confirm Password <span class="text-danger">*</span></label>
              <div class="position-relative mt-1">
                <input
                    v-model="dataParams.confirmPassword"
                    :type="dataParams.showConfirmPassword ? 'text' : 'password'"
                    placeholder="Confirm new password"
                    autocomplete="on"
                    class="form-control form-control-lg font_detail bg-light border-0"
                    style="padding-right: 45px;"
                    @keyup="checkConfirmPassword"
                />
                <span class="position-absolute" style="right: 15px; top: 50%; transform: translateY(-50%); cursor: pointer; color: #6c757d;"
                       @click="dataParams.showConfirmPassword = !dataParams.showConfirmPassword">
                  <font-awesome-icon :icon="['fas', dataParams.showConfirmPassword ? 'eye-slash' : 'eye']" />
                </span>
              </div>
              <Error v-if="errors.confirmPassword" v-bind:error="errors.confirmPassword"/>
              <p v-if="dataParams.confirmPasswordValidation" class="text-danger text-sm mt-1 mb-0">New Password and Confirm Password doesn't match</p>
            </div>
            <div class="form-group d-flex justify-content-end align-items-center">
              <button @click.prevent="updatePassword" class="btn btn-primary px-4 py-2" type="submit">Update Password</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { reactive } from "@vue/reactivity";   
import {inject, watch} from "vue";
import store from "@/store";
import useInputValidation from "@/modules/useInputValidations";

export default {
  name: "Profile",
  setup() {
    const showLoader = inject("showLoader");
    const hideLoader = inject("hideLoader");
    let {passwordRequiredValidation, passwordValidation, errors} = useInputValidation();

    const dataParams = reactive({
      oldPassword: '',
      newPassword: '',
      confirmPassword: '',
      confirmPasswordValidation: false,
      showOldPassword: false,
      showNewPassword: false,
      showConfirmPassword: false,
    })

    watch(
      () => dataParams.oldPassword,
      () => {
        passwordRequiredValidation("oldPassword", dataParams.oldPassword, "Old password");
      }
    );

    watch(
      () => dataParams.newPassword,
      () => {
        passwordValidation("newPassword", dataParams.newPassword);
      }
    );
    watch(
      () => dataParams.confirmPassword,
      () => {
        passwordValidation("confirmPassword", dataParams.confirmPassword);
      }
    );

    const validate = () => {
      Object.keys(errors).forEach(function (key) {
        errors[key] = "";
      });
      passwordRequiredValidation("oldPassword", dataParams.oldPassword, "Old password");
      passwordValidation("newPassword", dataParams.newPassword);
      passwordValidation("confirmPassword", dataParams.confirmPassword);
      return !(
          Object.values(errors).length <= 0 ||
          !Object.values(errors).every((x) => x === null || x === "")
      );
    };

    function checkConfirmPassword() {
      if (dataParams.confirmPassword !== '') {
        if (dataParams.newPassword !== dataParams.confirmPassword) {
          dataParams.confirmPasswordValidation = true;
        } else {
          dataParams.confirmPasswordValidation = false;
        }
      }    
    }

    const updatePassword = () => {
      if (validate()) {
        if (dataParams.newPassword !== dataParams.confirmPassword) return; 

        showLoader();
        const payload = {
          oldPassword: dataParams.oldPassword,
          newPassword: dataParams.newPassword,
        };

        store
          .dispatch("updateUserPassword", payload)
          .then(() => {
            hideLoader();
          })
          .catch((error) => {
            console.log(error);
            hideLoader();
          });
      }
    }

    return {
      dataParams,
      errors,
      checkConfirmPassword,
      updatePassword
    }
  }
  
}
</script>

<style scoped>
.avatar-circle {
  width: 90px;
  height: 90px;
  border-radius: 50%;
}
.list-group-item.active {
  background-color: #e9ecef;
  color: #0d6efd !important;
  font-weight: 600;
}
.list-group-item.active .icon-wrapper {
  background-color: #0d6efd;
  color: white;
}
.list-group-item .icon-wrapper {
  width: 32px;
  height: 32px;
  background-color: #f8f9fa;
  color: #6c757d;
  transition: all 0.2s ease-in-out;
}
.list-group-item:hover:not(.active) {
  background-color: #f8f9fa;
  color: #212529 !important;
}
.list-group-item:hover:not(.active) .icon-wrapper {
  background-color: #e9ecef;
  color: #212529;
}
.form-control:focus {
  border-color: #0d6efd;
  box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
  background-color: #fff !important;
}
</style>